import { useEffect, useRef, useState } from 'react'
import { Outlet, Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  MapPin,
  Package,
  ShoppingCart,
  ClipboardList,
  MessageSquare,
  Settings,
  HelpCircle,
  User,
  Warehouse,
  Route,
  AlertTriangle,
  Menu,
  X,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Truck,
  UserCog,
  Wallet,
  ShoppingBag,
  PackagePlus,
  TrendingUp,
  ArrowLeftRight,
  Megaphone,
  type LucideIcon,
} from 'lucide-react'
import { useAuth, getRoleLabel } from '../hooks/useAuth'
import { messagesApi } from '../services/api'

const UNREAD_POLL_INTERVAL_MS = 15000
const PANELS_REFRESH_INTERVAL_MS = 2 * 60 * 1000
const NAV_COLLAPSED_STORAGE_KEY = 'nav_collapsed'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Only the messages link carries a live unread badge today. */
  badgeKey?: 'messages'
}

const vendedorNavItems: NavItem[] = [
  { to: '/dashboard', label: 'Panel Principal', icon: LayoutDashboard },
  { to: '/customers', label: 'Clientes', icon: Users },
  { to: '/tracking', label: 'Seguimiento', icon: MapPin },
  { to: '/products', label: 'Productos', icon: Package },
  { to: '/sales', label: 'Ventas', icon: ShoppingCart },
  { to: '/orders', label: 'Pedidos', icon: ClipboardList },
  { to: '/routes', label: 'Rutas', icon: Route },
]

const panelNavItems: Record<'logistica' | 'rrhh' | 'finanzas', NavItem> = {
  logistica: { to: '/logistica', label: 'Logística', icon: Truck },
  rrhh: { to: '/rrhh', label: 'Recursos Humanos', icon: UserCog },
  finanzas: { to: '/finanzas', label: 'Finanzas', icon: Wallet },
}

const commonTailNavItems: NavItem[] = [
  { to: '/messages', label: 'Mensajes', icon: MessageSquare, badgeKey: 'messages' },
  { to: '/settings', label: 'Configuración', icon: Settings },
  { to: '/help', label: 'Ayuda', icon: HelpCircle },
  { to: '/profile', label: 'Perfil', icon: User },
]

/**
 * Builds the collaborator nav from their granted panels. A missing/undefined
 * `panels` field is treated as "has vendedor" for backward compatibility with
 * sessions issued before panels existed.
 */
function buildCollaboratorNavItems(panels: string[] | undefined): NavItem[] {
  const hasVendedor = panels === undefined || panels.includes('vendedor')
  const items: NavItem[] = []
  if (hasVendedor) items.push(...vendedorNavItems)
  ;(['logistica', 'rrhh', 'finanzas'] as const).forEach((panel) => {
    if (panels?.includes(panel)) items.push(panelNavItems[panel])
  })
  items.push(...commonTailNavItems)
  return items
}

const adminNavItems: NavItem[] = [
  { to: '/admin', label: 'Panel Principal', icon: LayoutDashboard },
  { to: '/customers', label: 'Clientes', icon: Users },
  { to: '/admin/products', label: 'Productos', icon: Package },
  { to: '/admin/production', label: 'Logística', icon: Warehouse },
  { to: '/admin/sales', label: 'Ventas', icon: ShoppingCart },
  { to: '/admin/orders', label: 'Pedidos', icon: ClipboardList },
  { to: '/admin/routes', label: 'Rutas', icon: Route },
  { to: '/admin/complaints', label: 'Quejas', icon: AlertTriangle },
  { to: '/admin/collaborators', label: 'Colaboradores', icon: Users },
  { to: '/admin/finanzas', label: 'Finanzas', icon: Wallet },
  { to: '/admin/messages', label: 'Mensajes', icon: MessageSquare, badgeKey: 'messages' },
  { to: '/choose-business', label: 'Cambiar de Negocio', icon: ArrowLeftRight },
  { to: '/settings', label: 'Configuración', icon: Settings },
  { to: '/help', label: 'Ayuda', icon: HelpCircle },
  { to: '/profile', label: 'Perfil', icon: User },
]

// FX Admin -- a second, unrelated small business the admin manages from this
// same portal (distribution_fx schema, admin-only today). Same Layout shell,
// a different nav list swapped in based on the current path -- see isFx
// below. No seller/collaborator ever sees this, only the admin account.
const fxNavItems: NavItem[] = [
  { to: '/fx/dashboard', label: 'Panel Principal', icon: LayoutDashboard },
  { to: '/fx/orders', label: 'Solicitudes', icon: ClipboardList },
  { to: '/fx/sales', label: 'Ventas', icon: ShoppingCart },
  { to: '/fx/products', label: 'Productos', icon: Package },
  { to: '/fx/purchase-orders', label: 'Órdenes de Compra', icon: PackagePlus },
  { to: '/fx/purchases', label: 'Compras', icon: ShoppingBag },
  { to: '/fx/suppliers', label: 'Proveedores', icon: Truck },
  { to: '/fx/customers', label: 'Clientes', icon: Users },
  { to: '/fx/locations', label: 'Ubicaciones', icon: MapPin },
  { to: '/fx/service-costs', label: 'Costos de Servicios', icon: Megaphone },
  { to: '/fx/finanzas', label: 'Finanzas', icon: Wallet },
  { to: '/choose-business', label: 'Cambiar de Negocio', icon: ArrowLeftRight },
  { to: '/settings', label: 'Configuración', icon: Settings },
  { to: '/help', label: 'Ayuda', icon: HelpCircle },
  { to: '/profile', label: 'Perfil', icon: User },
]

function SidebarLink({
  item,
  collapsed,
  unreadCount,
  onNavigate,
}: {
  item: NavItem
  collapsed: boolean
  unreadCount: number
  onNavigate: () => void
}) {
  const Icon = item.icon
  const badge = item.badgeKey === 'messages' ? unreadCount : 0
  const accessibleLabel = badge > 0 ? `${item.label} (${badge} sin leer)` : item.label

  return (
    <NavLink
      to={item.to}
      end
      onClick={onNavigate}
      title={accessibleLabel}
      aria-label={accessibleLabel}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
          collapsed ? 'lg:justify-center' : ''
        } ${
          isActive
            ? 'bg-terracotta-50 text-terracotta-700'
            : 'text-espresso-600 hover:bg-cream-100 hover:text-espresso-900'
        }`
      }
    >
      <span className="relative inline-flex shrink-0">
        <Icon className="h-5 w-5" aria-hidden="true" />
        {badge > 0 && (
          <>
            <span
              aria-hidden="true"
              className={`absolute -right-1 -top-1 hidden rounded-full bg-terracotta-600 ${
                collapsed ? 'lg:block lg:h-2.5 lg:w-2.5' : ''
              }`}
            />
            <span
              aria-hidden="true"
              className={`absolute -right-2 -top-1.5 min-w-[1rem] rounded-full bg-terracotta-600 px-1 text-center text-xs font-semibold leading-4 text-white ${
                collapsed ? 'lg:hidden' : ''
              }`}
            >
              {badge > 9 ? '9+' : badge}
            </span>
          </>
        )}
      </span>
      <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
    </NavLink>
  )
}

function SidebarFooter({
  userName,
  userRole,
  collapsed,
  onLogout,
}: {
  userName: string
  userRole: string
  collapsed: boolean
  onLogout: () => void
}) {
  return (
    <div className="border-t border-cream-200 p-2">
      <Link
        to="/profile"
        title={`${userName} (${userRole})`}
        className={`flex items-center gap-3 rounded-md px-2 py-2 text-sm text-espresso-700 hover:bg-cream-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
          collapsed ? 'lg:justify-center' : ''
        }`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-espresso-100 text-espresso-700">
          <User className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className={`min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
          <span className="block truncate font-medium">{userName}</span>
          <span className="block truncate text-xs text-espresso-500">{userRole}</span>
        </span>
      </Link>
      <button
        type="button"
        onClick={onLogout}
        title="Cerrar sesión"
        aria-label="Cerrar sesión"
        className={`mt-1 flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-espresso-600 hover:bg-terracotta-50 hover:text-terracotta-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
          collapsed ? 'lg:justify-center' : ''
        }`}
      >
        <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className={collapsed ? 'lg:hidden' : ''}>Cerrar Sesión</span>
      </button>
    </div>
  )
}

export default function Layout() {
  const { user, logout, loading, refreshPanels } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [unreadCount, setUnreadCount] = useState(0)
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(NAV_COLLAPSED_STORAGE_KEY) === 'true'
  )
  const [mobileOpen, setMobileOpen] = useState(false)
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null)
  const mobileDrawerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login')
    }
  }, [user, loading, navigate])

  useEffect(() => {
    if (!user) return
    const fetchUnread = async () => {
      try {
        const res = await messagesApi.unreadCount()
        setUnreadCount(res.data.unread_count)
      } catch {
        // Non-critical — leave the badge as-is on a transient failure.
      }
    }
    fetchUnread()
    const interval = setInterval(fetchUnread, UNREAD_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [user])

  useEffect(() => {
    localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, String(collapsed))
  }, [collapsed])

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Revalidate the collaborator's granted panels on every navigation, so a
  // panel revoked mid-session stops showing in the sidebar without a
  // re-login.
  useEffect(() => {
    if (!user) return
    refreshPanels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname])

  // Fallback for a user who stays on one page a long time: revalidate
  // panels periodically too, not just on navigation.
  useEffect(() => {
    if (!user) return
    const interval = setInterval(refreshPanels, PANELS_REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Close on Escape, trap Tab focus inside the drawer, and keep the page
  // from scrolling behind it.
  useEffect(() => {
    if (!mobileOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      const drawer = mobileDrawerRef.current
      if (!drawer) return
      const focusable = drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'
    mobileCloseButtonRef.current?.focus()
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-cream-100">
        <div className="text-xl text-espresso-600">Cargando...</div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  const isAdmin = user.role === 'admin'
  const isFx = location.pathname.startsWith('/fx')
  const navItems = isAdmin ? (isFx ? fxNavItems : adminNavItems) : buildCollaboratorNavItems(user.panels)
  const brandIcon = isFx ? <TrendingUp className="h-6 w-6 shrink-0 text-terracotta-700" aria-hidden="true" /> : <span className="text-2xl shrink-0" aria-hidden="true">☕</span>
  const brandLabel = isFx ? 'FX Admin' : 'Cafe Distribution'

  return (
    <div className="min-h-[100dvh] bg-cream-100">
      {/* Mobile top bar */}
      <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-cream-200 bg-cream-50 px-4 shadow-sm lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menú de navegación"
          className="relative rounded-md p-1.5 text-espresso-600 hover:bg-cream-100 hover:text-espresso-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          <Menu className="h-6 w-6" aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute right-1 top-1 h-2 w-2 rounded-full bg-terracotta-600"
            />
          )}
        </button>
        <Link to="/" className="flex items-center gap-2">
          {brandIcon}
          <span className="text-base font-bold text-terracotta-700">{brandLabel}</span>
        </Link>
      </div>

      {/* Desktop sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-cream-200 bg-cream-50 transition-[width] duration-200 lg:flex ${
          collapsed ? 'lg:w-20' : 'lg:w-64'
        }`}
      >
        <div className="flex h-16 items-center justify-between border-b border-cream-200 px-4">
          <Link to="/" className="flex items-center gap-2 overflow-hidden">
            {brandIcon}
            <span
              className={`truncate text-lg font-bold text-terracotta-700 ${
                collapsed ? 'lg:hidden' : ''
              }`}
            >
              {brandLabel}
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            className="shrink-0 rounded-md p-1.5 text-espresso-500 hover:bg-cream-100 hover:text-espresso-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
          >
            {collapsed ? (
              <ChevronsRight className="h-5 w-5" aria-hidden="true" />
            ) : (
              <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        <nav aria-label="Navegación principal" className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
          {navItems.map((item) => (
            <SidebarLink
              key={item.to}
              item={item}
              collapsed={collapsed}
              unreadCount={unreadCount}
              onNavigate={() => {}}
            />
          ))}
        </nav>
        <SidebarFooter
          userName={user.name}
          userRole={getRoleLabel(user.role, user.panels)}
          collapsed={collapsed}
          onLogout={logout}
        />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          {/* Literal hex, not bg-espresso-950 -- a backdrop scrim should stay
              dark in both themes; the espresso CSS variable flips light in
              dark mode (see index.css), which would make this invisible. */}
          <div
            className="fixed inset-0 bg-[#1c0f0a]/60"
            aria-hidden="true"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            ref={mobileDrawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menú de navegación"
            className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-cream-50 shadow-xl"
          >
            <div className="flex h-16 items-center justify-between border-b border-cream-200 px-4">
              <Link
                to="/"
                className="flex items-center gap-2"
                onClick={() => setMobileOpen(false)}
              >
                {brandIcon}
                <span className="text-lg font-bold text-terracotta-700">{brandLabel}</span>
              </Link>
              <button
                ref={mobileCloseButtonRef}
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Cerrar menú de navegación"
                className="rounded-md p-1.5 text-espresso-500 hover:bg-cream-100 hover:text-espresso-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Navegación principal" className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
              {navItems.map((item) => (
                <SidebarLink
                  key={item.to}
                  item={item}
                  collapsed={false}
                  unreadCount={unreadCount}
                  onNavigate={() => setMobileOpen(false)}
                />
              ))}
            </nav>
            <SidebarFooter
              userName={user.name}
              userRole={getRoleLabel(user.role, user.panels)}
              collapsed={false}
              onLogout={logout}
            />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-[margin] duration-200 ${collapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <main className="mx-auto max-w-screen-2xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
